import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  IconButton,
  Badge,
  Tooltip,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LoginOutlined from "@mui/icons-material/LoginOutlined";
import logo from "../static/media/logo_menu_darck.webp";
import { AppContext } from "../context/AppContext";
import Search from "./Navbar/Search";
import NavbarMobile, { MobileSearch } from "./Navbar/NavbarMobile";
import "./Navbar.css";

export default function Navbar() {
  const {
    cartItems = [],
    clearCart,
    logoutUser,
    isAuthenticated,
    authLoading,
    isAdminAccount,
    accountType,
    activeAccount,
  } = useContext(AppContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);

  const cartCount = cartItems.reduce((s, i) => s + Number(i.quantity || 0), 0);
  const showLogged = !authLoading && !!isAuthenticated;

  const resolvedAccountType = accountType || activeAccount?.type || "";
  const adminAccount = isAdminAccount || resolvedAccountType === "admin";

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    if (logoutUser) await logoutUser();
    window.dispatchEvent(new Event("auth-change"));
    setAccountOpen(false);
    if (clearCart) clearCart();
    navigate("/");
  };

  const handleAccount = () => {
    setAccountOpen(false);
    navigate(adminAccount ? "/admin-panel" : "/user-panel");
  };

  const handleLogin = () => {
    setAccountOpen(false);
    navigate("/login");
  };

  useEffect(() => {
    const onClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };

    document.addEventListener("mousedown", onClick);

    return () => {
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  return (
    <header className="nav-wrap">
      <div className="nav-container">
        <div className="nav-row">
          <Link to="/" className="nav-logo" aria-label="Inicio">
            <img src={logo} alt="CM SOLTEC" />
          </Link>

          {!isMobile && (
            <div className="nav-search">
              <Search isMobile={false} />
            </div>
          )}

          <div className="nav-icons">
            {isMobile ? (
              <NavbarMobile
                cartCount={cartCount}
                showLogged={showLogged}
                accountType={resolvedAccountType}
                isActive={isActive}
                navigate={navigate}
                onLogin={handleLogin}
                onLogout={handleLogout}
                onAccount={handleAccount}
              />
            ) : (
              <>
                <Tooltip title="Carrito" arrow>
                  <Link to="/shop" className="icon-link" aria-label="Carrito">
                    <Badge badgeContent={cartCount} color="error">
                      <ShoppingCartIcon />
                    </Badge>
                  </Link>
                </Tooltip>

                <div className="account" ref={accountRef}>
                  <Tooltip title={showLogged ? "Cuenta" : "Iniciar sesión"} arrow>
                    <IconButton
                      className="icon-btn"
                      aria-label="Cuenta"
                      onClick={() => setAccountOpen((o) => !o)}
                    >
                      {showLogged ? <AccountCircleIcon /> : <LoginOutlined />}
                    </IconButton>
                  </Tooltip>

                  <div className={`account-dropdown ${accountOpen ? "open" : ""}`}>
                    {showLogged ? (
                      <>
                        <button onClick={handleAccount} className="dropdown-item">
                          Mi Cuenta
                        </button>
                        <button onClick={handleLogout} className="dropdown-item">
                          Cerrar Sesión
                        </button>
                      </>
                    ) : (
                      <button onClick={handleLogin} className="dropdown-item">
                        Iniciar Sesión
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {isMobile && <MobileSearch />}

        <nav className="nav-links">
          <Button
            className={`nav-btn ${isActive("/") ? "active" : ""}`}
            onClick={() => navigate("/")}
          >
            INICIO
          </Button>
          <Button
            className={`nav-btn ${isActive("/acerca-de-nosotros") ? "active" : ""}`}
            onClick={() => navigate("/acerca-de-nosotros")}
          >
            NOSOTROS
          </Button>
          <Button
            className={`nav-btn ${isActive("/servicios") ? "active" : ""}`}
            onClick={() => navigate("/servicios")}
          >
            SERVICIOS
          </Button>
          <Button
            className={`nav-btn ${isActive("/categorias") ? "active" : ""}`}
            onClick={() => navigate("/categorias")}
          >
            TIENDA
          </Button>
          <Button
            className={`nav-btn ${isActive("/contacto") ? "active" : ""}`}
            onClick={() => navigate("/contacto")}
          >
            CONTACTO
          </Button>
        </nav>
      </div>
    </header>
  );
}